import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ShoppingCart, ArrowLeft, Plus, Minus, Package, Ruler, FileText, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Produsul nu a fost găsit');
      navigate('/magazin');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product && quantity > 0) {
      addToCart(product, quantity);
      toast.success(`${quantity} x ${product.name} adăugat în coș`);
    }
  };

  const getAllImages = () => {
    if (!product) return [];
    const images = product.images && product.images.length > 0 
      ? product.images 
      : [product.image_url];
    return images.filter(img => img && !img.includes('no_image'));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50">
        <Navbar />
        <div className="pt-24 pb-12 px-4 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const images = getAllImages();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="product-detail-page">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-6" data-testid="breadcrumb">
            <Link to="/" className="text-slate-500 hover:text-cyan-600">Acasă</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <Link to="/magazin" className="text-slate-500 hover:text-cyan-600">Magazin</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-900 font-medium truncate max-w-xs">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              {/* Main Image */}
              <div className="glass-card p-4 aspect-square overflow-hidden rounded-2xl">
                <img
                  src={images[selectedImage] || product.image_url}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  data-testid="product-main-image"
                />
              </div>
              
              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div className="flex space-x-3 overflow-x-auto pb-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition ${
                        selectedImage === index ? 'border-cyan-500' : 'border-transparent'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Brand & Category */}
              <div className="flex items-center space-x-3">
                {product.brand && (
                  <span className="px-3 py-1 bg-cyan-100 text-cyan-700 text-sm font-medium rounded-full">
                    {product.brand}
                  </span>
                )}
                <span className="text-slate-500 text-sm">{product.category}</span>
              </div>

              {/* Title */}
              <h1 
                className="text-3xl lg:text-4xl font-bold text-slate-900" 
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="product-title"
              >
                {product.name}
              </h1>

              {/* SKU */}
              {product.sku && (
                <p className="text-sm text-slate-500">
                  Cod produs: <span className="font-medium">{product.sku}</span>
                </p>
              )}

              {/* Price */}
              <div className="flex items-baseline space-x-3">
                <span 
                  className="text-4xl font-bold text-orange-500"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                  data-testid="product-price"
                >
                  {product.price.toFixed(2)} LEI
                </span>
                <span className="text-slate-500">/ {product.unit}</span>
              </div>
              <p className="text-sm text-slate-500">Preț cu TVA inclus</p>

              {/* Short Description */}
              {product.short_description && (
                <p className="text-lg text-slate-600">{product.short_description}</p>
              )}

              {/* Stock Status */}
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className={`font-medium ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {product.stock > 0 ? 'În stoc' : 'Stoc epuizat'}
                </span>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center border rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-slate-100 transition"
                    data-testid="decrease-qty"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <Input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 text-center border-0 focus:ring-0"
                    data-testid="quantity-input"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 hover:bg-slate-100 transition"
                    data-testid="increase-qty"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <Button
                  onClick={handleAddToCart}
                  className="btn-primary flex-1 py-6 text-lg"
                  disabled={product.stock <= 0}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Adaugă în Coș
                </Button>
              </div>

              {/* Total Price */}
              <div className="glass-card p-4 bg-orange-50 border-orange-200">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Total pentru {quantity} {product.unit}:</span>
                  <span className="text-2xl font-bold text-orange-500">
                    {(product.price * quantity).toFixed(2)} LEI
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Product Details Tabs */}
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Description */}
            <div className="lg:col-span-2 glass-card p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <FileText className="w-6 h-6 mr-3 text-cyan-500" />
                Descriere Produs
              </h2>
              <div className="prose prose-slate max-w-none" data-testid="product-description">
                <p className="text-slate-600 whitespace-pre-line leading-relaxed">
                  {product.description}
                </p>
              </div>
            </div>

            {/* Specifications */}
            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Ruler className="w-6 h-6 mr-3 text-cyan-500" />
                Specificații
              </h2>
              {product.specifications && Object.keys(product.specifications).length > 0 ? (
                <div className="space-y-4" data-testid="product-specs">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-3 border-b border-slate-100 last:border-0">
                      <span className="text-slate-500">{key}</span>
                      <span className="font-medium text-slate-900">{value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Specificații indisponibile</p>
                </div>
              )}
            </div>
          </div>

          {/* Back Button */}
          <div className="mt-8">
            <Link to="/magazin">
              <Button variant="outline" data-testid="back-to-shop">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Înapoi la Magazin
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
